import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/api";
import { useLang } from "@/i18n";
import SearchBar from "@/components/SearchBar";
import Reveal from "@/components/Reveal";

function PropertyCard({ p, t, eager = false }) {
  const weeklies = [p.weekly_price, ...(p.price_periods || []).filter((x) => x.per === "week").map((x) => x.price)].filter((v) => v != null && v > 0);
  const nightlies = [p.nightly_price, ...(p.price_periods || []).filter((x) => x.per === "night").map((x) => x.price)].filter((v) => v != null && v > 0);
  const minPrice = weeklies.length ? Math.min(...weeklies) : (nightlies.length ? Math.min(...nightlies) : null);
  const isNightly = !weeklies.length && nightlies.length > 0;
  return (
    <Link
      to={`/locations/${p.slug}`}
      data-testid={`property-card-${p.slug}`}
      className="group block rounded-md bg-white p-4 shadow-[0_10px_30px_-16px_rgba(42,51,35,0.25)] transition-all duration-300 hover:shadow-[0_18px_44px_-18px_rgba(42,51,35,0.35)]"
    >
      <h3 className="pb-3 font-serif italic font-semibold text-xl sm:text-2xl text-maquis-deep">{p.name}</h3>
      <div className="overflow-hidden rounded-sm bg-ivoire">
        <img
          src={p.cover_image || p.images[0]} alt={p.name} loading={eager ? "eager" : "lazy"} fetchpriority={eager ? "high" : undefined} decoding="async"
          className="aspect-[16/10] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
      </div>
      <dl className="mt-4 space-y-1 text-xs text-maquis-olive">
        <div className="flex gap-1">
          <dt className="font-semibold text-maquis-deep">{t.gallery.place} :</dt>
          <dd>{p.location.split(",")[0]}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="font-semibold text-maquis-deep">{t.gallery.surface} :</dt>
          <dd>{p.surface ? `${p.surface} m²` : "—"}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="font-semibold text-maquis-deep">{t.gallery.people} :</dt>
          <dd>2-{p.guests}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="font-semibold text-maquis-deep">{t.gallery.rooms} :</dt>
          <dd>{p.bedrooms}</dd>
        </div>
      </dl>
      {minPrice && (
        <p data-testid={`property-price-${p.slug}`} className="mt-3 text-sm font-semibold text-maquis-deep">
          {t.gallery.price_from} {minPrice.toLocaleString("fr-FR")} € / {isNightly ? t.gallery.per_night : t.gallery.per_week}
        </p>
      )}
    </Link>
  );
}

export default function LocationsPage() {
  const { t, simg } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProperties, setAllProperties] = useState(null);

  const type = searchParams.get("type") || "";
  const guests = searchParams.get("guests") || "";
  const pool = searchParams.get("pool") || "";
  const secteur = searchParams.get("secteur") || "";

  useEffect(() => {
    api.get("/properties").then((r) => setAllProperties(r.data)).catch(() => setAllProperties([]));
  }, []);

  const applyFilters = (next) => {
    const params = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) params.set(k, v); });
    setSearchParams(params, { replace: true });
  };

  const properties = allProperties === null ? null : allProperties.filter((p) => {
    if (type && p.type !== type) return false;
    if (guests && p.guests < Number(guests)) return false;
    if (pool === "oui" && !p.pool) return false;
    if (pool === "non" && p.pool) return false;
    if (secteur && !p.location.toLowerCase().includes(secteur.toLowerCase())) return false;
    return true;
  });

  return (
    <main data-testid="locations-page">
      <header className="relative flex h-[55vh] min-h-[420px] items-end overflow-hidden">
        <img src={simg("coastAerial")} alt="Paysage de Corse-du-Sud" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-mer/80 via-mer/30 to-mer/40" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 text-casse">
          <p className="eyebrow text-sable">{t.locations_page.eyebrow}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl">{t.locations_page.title}</h1>
          <p className="mt-4 text-justify text-sm sm:text-base text-casse/85">{t.locations_page.sub}</p>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-14 sm:pt-20 pb-28 sm:pb-36">
        <SearchBar
          live
          initial={{ type, guests, pool, secteur }}
          onFilter={(f) => applyFilters(f)}
        />

        <div className="mt-28" data-testid="properties-grid">
          {properties === null ? (
            <p className="text-maquis-soft">{t.auth.checking}</p>
          ) : properties.length === 0 ? (
            <p data-testid="no-results-message" className="py-16 text-center font-serif italic text-2xl text-maquis-soft">
              {t.locations_page.no_results}
            </p>
          ) : (
            <div className="grid gap-x-10 gap-y-16 sm:grid-cols-2">
              {properties.map((p, i) => (
                <Reveal key={p.id} delay={(i % 2) * 0.08}>
                  <PropertyCard p={p} t={t} eager={i < 2} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
